import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, michiganSoSBundleValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { useEffect, useState } from 'react'

interface MichiganSOSBundlesAddEditDialogProps {
  initialValues: MichiganSOSBundleDto
  open: boolean
  isLoading: boolean
  onClose: () => void
  onSave: (values: MichiganSOSBundleDto) => void
}

export default function MichiganSOSBundleAddEditDialog({
  initialValues,
  isLoading,
  onClose,
  onSave,
  open
}: MichiganSOSBundlesAddEditDialogProps) {
  const [owners, setOwners] = useState<string[]>([])
  const [loadingState, setLoadingState] = useLoadingState({ isGettingOwners: false })

  useEffect(() => {
    controllers.michiganSoSBundles.getOwners().then(res => {
      setOwners(res.value)
    })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={michiganSoSBundleValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.michiganSOSBundleGuid ? 'Edit' : 'Add'} Bundle</DialogTitle>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('michiganTitleOwnerName', value || undefined)
                      }}
                      options={owners}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.michiganTitleOwnerName && errors.michiganTitleOwnerName)}
                          helperText={touched.michiganTitleOwnerName && errors.michiganTitleOwnerName}
                          label='Owner'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingOwners && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={values.michiganTitleOwnerName}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Owner Drop-Down:**

The Owner Drop-Down lists all “Owner Name” fields alphabetically.

Data is sourced from the Vehicle Details – Titles (tab) for vehicles that meet
the following conditions:
   - Title Application State is set to Michigan.
   - Application Packet Ready For MI SoS Bundle field is populated.
   - The vehicle is not associated with any MI SoS Bundle.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                  Cancel
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
